import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
} from "@components/Button";
import { Icon, IconDisplay, IconSize } from "@components/Icon";
import { Modal } from "@components/Modal";
import { NpsCheckboxes } from "@components/Nps/NpsCheckboxes";
import { NpsOpenText } from "@components/Nps/NpsOpenText";
import { NpsScore } from "@components/Nps/NpsScore";
import { css } from "@emotion/react";
import React from "react";

import { useNpsContext } from "src/context/NpsContext";
import { hideNps } from "src/context/NpsContext/actions";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { horizontalStackCss, spacing } from "src/theme/spacing";
import { FontWeight } from "src/theme/text";

const modalContentCss = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.s};
  min-width: unset;
  padding: ${spacing.l};
`;

const modalBodyCss = css`
  min-width: unset;
  height: unset;

  width: calc(100vw - ${spacing.l} * 2);

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      min-width: unset;
      width: 420px;
    `,
  })};
`;

const NpsModalHeader: React.FCC = () => {
  const { currentStep } = useNpsContext();
  const isThankYouStep = currentStep === 3;

  return (
    <div css={[horizontalStackCss.xs, { alignItems: "center" }]}>
      <Icon
        iconImport={() => import("@components/Icon/icons/MessageSquare")}
        display={IconDisplay.SECONDARY}
        size={IconSize.SMALL}
      />
      <span
        css={{
          fontWeight: FontWeight.BOLD,
          color: `var(${
            colorCssVars.text[isThankYouStep ? "body" : "secondary"]
          })`,
        }}
      >
        {isThankYouStep ? "Thank you!" : "Share your thoughts"}
      </span>
      <Button
        data-tname="close_nps_modal"
        role={ButtonRole.UNSTYLED}
        size={ButtonSize.MEDIUM}
        css={{ marginLeft: "auto" }}
        onClick={{
          kind: ButtonTargetKind.FUNCTION,
          action: hideNps,
        }}
      >
        <Icon
          iconImport={() => import("@components/Icon/icons/XIcon")}
          display={IconDisplay.ACCENT}
          size={IconSize.MEDIUM}
        />
      </Button>
    </div>
  );
};

const NpsModalContent = () => {
  const { currentStep } = useNpsContext();

  switch (currentStep) {
    case 0:
      return <NpsScore />;
    case 1:
      return <NpsCheckboxes />;
    case 2:
      return <NpsOpenText />;
    default:
      return null;
  }
};

export const NpsModal: React.FCC = () => {
  const { visible } = useNpsContext();

  const modalStyle = {
    overlay: {
      backgroundColor: "rgba(46, 52, 52, 0.6)",
      transition: "unset",
      padding: 0,
    },
    content: {
      position: "absolute" as const,
      bottom: 0,
      right: 0,
      left: "auto",
      top: "auto",
      transform: "none",
      maxWidth: `calc(100vw - ${spacing.l} * 2)`,
      minWidth: "unset",
      transition: "unset",
      padding: 0,
      margin: spacing.l,
      width: "unset",
      height: "unset",
      borderRadius: "16px",
    },
  };

  return (
    <Modal
      isOpen={visible}
      onRequestClose={hideNps}
      showHeader={false}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      noExit={false}
      contentCss={modalContentCss}
      modalBodyCss={modalBodyCss}
      style={modalStyle}
    >
      <NpsModalHeader />
      <NpsModalContent />
    </Modal>
  );
};
